
import { supabase } from '@/integrations/supabase/client';
import { Appointment, Metrics, AppointmentStatus, AppointmentNotes, ViewPeriod, PaymentStatus } from '@/types/dashboard';

/**
 * Serviço para gerenciar consultas e métricas do dashboard do psicólogo
 */
export class DashboardService {
  /**
   * Busca consultas do psicólogo com filtros
   * @param psychologistId ID do psicólogo
   * @param period Período de visualização (dia, semana, mês)
   * @param page Número da página para paginação
   * @param pageSize Tamanho da página para paginação
   */
  static async fetchAppointments(
    psychologistId: string, 
    period: ViewPeriod = 'week',
    page: number = 1,
    pageSize: number = 50
  ): Promise<Appointment[]> {
    try {
      // Calcula datas de início e fim com base no período
      const today = new Date();
      let startDate = new Date(today);
      let endDate = new Date(today);
      
      switch (period) {
        case 'day':
          // Mantém apenas o dia atual
          break;
        case 'week':
          // Define início como domingo e fim como sábado da semana atual
          const dayOfWeek = today.getDay();
          startDate.setDate(today.getDate() - dayOfWeek);
          endDate.setDate(startDate.getDate() + 6);
          break;
        case 'month':
          // Define início como primeiro dia do mês e fim como último dia
          startDate.setDate(1);
          endDate.setMonth(today.getMonth() + 1);
          endDate.setDate(0);
          break;
      }
      
      // Formata datas para string ISO
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Calcula offset para paginação
      const offset = (page - 1) * pageSize;
      
      // Realiza a consulta com filtros e paginação
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          user_id,
          date,
          time,
          duration,
          status,
          payment_status,
          notes,
          created_at,
          updated_at
        `)
        .eq('psychologist_id', psychologistId)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .range(offset, offset + pageSize - 1);
      
      if (error) {
        console.error('Error fetching appointments:', error);
        throw error;
      }
      
      // Mapeia os resultados para o formato esperado
      return data?.map(item => ({
        id: item.id,
        patientId: item.user_id,
        patientName: 'Paciente', // Nome será buscado separadamente se necessário
        patientPhoto: undefined,
        date: item.date,
        time: item.time,
        duration: item.duration || 50,
        status: item.status as AppointmentStatus,
        paymentStatus: (item.payment_status as PaymentStatus) || 'pending',
        notes: item.notes,
        preSessionNotes: '',
        postSessionNotes: '',
        createdAt: item.created_at,
        updatedAt: item.updated_at
      })) || [];
    } catch (error) {
      console.error('Error in fetchAppointments:', error);
      return [];
    }
  }
  
  /**
   * Busca métricas do psicólogo para o dashboard
   * @param psychologistId ID do psicólogo
   * @param period Período de visualização (dia, semana, mês)
   */
  static async fetchMetrics(psychologistId: string, period: ViewPeriod = 'week'): Promise<Metrics | null> {
    try {
      // Para agora, retorna dados simulados baseados no período
      // Em produção, isso seria substituído por uma função RPC no Supabase
      
      const baseMetrics = {
        day: {
          totalAppointments: 3,
          completedAppointments: 2,
          cancelledAppointments: 1,
          noShowAppointments: 0,
          attendanceRate: 67,
          attendanceRateTrend: 10,
          averageRating: 4.8,
          totalReviews: 2,
          revenue: {
            total: 450.00,
            pending: 150.00,
            received: 300.00
          }
        },
        week: {
          totalAppointments: 12,
          completedAppointments: 10,
          cancelledAppointments: 2,
          noShowAppointments: 0,
          attendanceRate: 83,
          attendanceRateTrend: 5,
          averageRating: 4.8,
          totalReviews: 8,
          revenue: {
            total: 1200.00,
            pending: 200.00,
            received: 1000.00
          }
        },
        month: {
          totalAppointments: 45,
          completedAppointments: 38,
          cancelledAppointments: 5,
          noShowAppointments: 2,
          attendanceRate: 84,
          attendanceRateTrend: -2,
          averageRating: 4.7,
          totalReviews: 25,
          revenue: {
            total: 4500.00,
            pending: 750.00,
            received: 3750.00
          }
        }
      };
      
      return baseMetrics[period];
    } catch (error) {
      console.error('Error in fetchMetrics:', error);
      return null;
    }
  }
  
  /**
   * Atualiza o status de uma consulta
   * @param appointmentId ID da consulta
   * @param status Novo status
   */
  static async updateAppointmentStatus(appointmentId: string, status: AppointmentStatus): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', appointmentId);
      
      if (error) {
        console.error('Error updating appointment status:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error in updateAppointmentStatus:', error);
      return false;
    }
  }
  
  /**
   * Adiciona ou atualiza notas de uma consulta
   * @param appointmentId ID da consulta
   * @param notes Objeto com notas pré e pós-sessão
   */
  static async updateAppointmentNotes(appointmentId: string, notes: AppointmentNotes): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          notes: JSON.stringify(notes),
          updated_at: new Date().toISOString()
        })
        .eq('id', appointmentId);
      
      if (error) {
        console.error('Error updating appointment notes:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error in updateAppointmentNotes:', error);
      return false;
    }
  }
  
  /**
   * Busca detalhes de uma consulta específica
   * @param appointmentId ID da consulta
   */
  static async getAppointmentDetails(appointmentId: string): Promise<Appointment | null> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          user_id,
          date,
          time,
          duration,
          status,
          payment_status,
          notes,
          created_at,
          updated_at
        `)
        .eq('id', appointmentId)
        .single();
      
      if (error) {
        console.error('Error fetching appointment details:', error);
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      // Mapeia o resultado para o formato esperado
      return {
        id: data.id,
        patientId: data.user_id,
        patientName: 'Paciente',
        patientPhoto: undefined,
        date: data.date,
        time: data.time,
        duration: data.duration || 50,
        status: data.status as AppointmentStatus,
        paymentStatus: (data.payment_status as PaymentStatus) || 'pending',
        notes: data.notes,
        preSessionNotes: '',
        postSessionNotes: '',
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      console.error('Error in getAppointmentDetails:', error);
      return null;
    }
  }
}

export default DashboardService;
